import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ContextStore from "../../hooks/ContextStore";
import "./scanner.css";
import { Alerts, Find, Header, MarjanPrep, Packing, Product, Returns, ScanInput, Verdict } from "./component";
import { CancelAlerts } from "./component/CancelAlerts";
import { AppMode } from "./component/header/Header.component";
import { Spinkit } from "./component/spinkit/Spinkit";
import { actions } from "./dispatch";
import { dispatch } from "./dispatch/dispatch";
import { countPieces, countProducts, statusAr, t } from "./i18n/ar";
import { IErrorResponse } from "./interfaces/responses/ErrorResponse.interface";
import { IOrderItemResponse, IOrderResponse } from "./interfaces/responses/OrderResponse.interface";
import { IScanResult } from "./interfaces/responses/ScanResult.interface";
import { IStoreResponseData } from "./interfaces/responses/StoreResponseData.interface";
import { playCaution, playGo, playNotFound, playStop, unlockAudio } from "./sound";

export type VerdictKind = "idle" | "go" | "stop" | "caution" | "notfound";

// Jumia sends ONE item per unit, so "quantity 2" arrives as two identical
// items. The worker must see ONE card with a big ×2 — never two cards.
export interface IItemGroup {
    key: string;
    sku: string;
    name: string;
    imageUrl: string;
    status: string;
    isRts: boolean;
    units: IOrderItemResponse[];
}

const RTS = "READY_TO_SHIP";

export const groupItems = (items: IOrderItemResponse[]): IItemGroup[] => {
    const map = new Map<string, IItemGroup>();
    for (const it of items) {
        const sku = String(it.product?.sellerSku || "");
        const status = String(it.status || "");
        const key = `${sku}|${status}`;
        const g = map.get(key);
        if (g) {
            g.units.push(it);
        } else {
            map.set(key, {
                key,
                sku,
                name: String(it.product?.name || ""),
                imageUrl: String(it.product?.imageUrl || ""),
                status,
                isRts: status === RTS,
                units: [it],
            });
        }
    }
    // Green (pack these) first, so the worker's eye lands on what to do.
    return Array.from(map.values()).sort((a, b) => Number(b.isRts) - Number(a.isRts));
};

function ScannerApp() {
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    // The day starts with PREPARING orders; scanning comes after the labels.
    const [mode, setMode] = useState<AppMode>("packing");
    const { contextStore } = useContext(ContextStore);
    const admin = contextStore?.user?.type === "admin";   // 🚨 alerts page: admin only for now (user 2026-10-21)
    const [stores, setStores] = useState<Array<IStoreResponseData>>([]);
    const [orderNumber, setOrderNumber] = useState<string>("");
    const [error, setError] = useState("");
    const [order, setOrder] = useState<IOrderResponse | null>(null);
    const [foundStore, setFoundStore] = useState<IStoreResponseData | null>(null);
    const [stockBySku, setStockBySku] = useState<Record<string, number | null>>({});
    const [urlBySku, setUrlBySku] = useState<Record<string, string>>({});
    const [groupBySku, setGroupBySku] = useState<Record<string, string>>({});
    const [variationBySku, setVariationBySku] = useState<Record<string, string>>({});
    const [packBySku, setPackBySku] = useState<Record<string, any>>({});
    const [lastCode, setLastCode] = useState<string>("");
    // Guards against an older in-flight lookup overwriting a newer scan
    const seqRef = useRef<number>(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const groups = useMemo(() => (order ? groupItems(order.items || []) : []), [order]);
    const totalUnits = order?.items?.length || 0;
    const rtsUnits = order ? order.items.filter((i) => i.status === RTS).length : 0;

    const verdict: VerdictKind = !order
        ? error
            ? "notfound"
            : "idle"
        : totalUnits > 0 && rtsUnits === totalUnits
        ? "go"
        : rtsUnits === 0
        ? "stop"
        : "caution";

    const announce = (kind: VerdictKind, o: IOrderResponse | null) => {
        const units = o?.items?.length || 0;
        const products = o ? groupItems(o.items || []).length : 0;
        switch (kind) {
            case "go":
                return playGo(`جاهز. ${countProducts(products)}. ${countPieces(units)}.`);
            case "stop": {
                const st = o?.items?.[0]?.status ? statusAr(String(o.items[0].status)) : "";
                return playStop(`توقف. لا ترسل. ${st}`);
            }
            case "caution":
                return playCaution("انتبه. أرسل الأخضر فقط.");
            case "notfound":
                return playNotFound("الطلب غير موجود. أعد المسح.");
        }
    };

    const applyResult = (result: IScanResult) => {
        const o = result.order;
        const units = o.items?.length || 0;
        const rts = (o.items || []).filter((i) => i.status === RTS).length;
        const kind: VerdictKind = units > 0 && rts === units ? "go" : rts === 0 ? "stop" : "caution";
        setOrder(o);
        setFoundStore(result.store);
        setStockBySku(result.stockBySku || {});
        setUrlBySku(result.urlBySku || {});
        setGroupBySku(result.groupBySku || {});
        setVariationBySku(result.variationBySku || {});
        setPackBySku(result.packBySku || {});
        setError("");
        setOrderNumber("");
        announce(kind, o);
    };

    const fail = (message: string) => {
        setOrder(null);
        setFoundStore(null);
        setError(message);
        setOrderNumber("");
        announce("notfound", null);
    };

    // Always hits the server — every scan returns the real-time status
    const lookupOrder = async (code: string) => {
        const cleaned = code.trim();
        const seq = ++seqRef.current;
        setLastCode(cleaned);
        setShowSpinner(true);
        const resp: IScanResult | IErrorResponse = await dispatch(actions.findOrder, {}, { code: cleaned });
        if (seq !== seqRef.current) return;
        setShowSpinner(false);
        if (!resp || "error" in resp) {
            const msg = (resp as IErrorResponse)?.error?.message || "";
            fail(/network/i.test(msg) ? t.networkError : t.notFoundTitle);
        } else {
            applyResult(resp);
        }
    };

    // 9-digit order numbers fire instantly as the scanner types them;
    // any other code (e.g. tracking number) fires on the scanner's Enter.
    // (2026-10-15) a barcode gun types a whole code in a few hundred ms; when it does not send Enter, the scan
    // goes in by itself once it stops typing. A person typing by hand (slower) still presses Enter.
    const gunRef = useRef<{ start: number; timer: any }>({ start: 0, timer: null });
    const onChangeOrderNumber = (value: string) => {
        unlockAudio();
        setOrderNumber(value);
        const cleaned = value.trim();
        clearTimeout(gunRef.current.timer);
        if (cleaned.length === 0) { gunRef.current.start = 0; return; }
        if (cleaned.length <= 1 || !gunRef.current.start) gunRef.current.start = Date.now();
        if (/^[0-9]{9}$/.test(cleaned)) { gunRef.current.start = 0; return lookupOrder(cleaned); }
        const fast = Date.now() - gunRef.current.start < 120 * cleaned.length && cleaned.length >= 9;
        const looksComplete = /DS-[A-Z0-9]{2,4}-\d{6,12}-\d{2,6}/i.test(cleaned);
        if (fast || looksComplete) gunRef.current.timer = setTimeout(() => { gunRef.current.start = 0; lookupOrder(cleaned); }, 180);
    };

    const onSubmitCode = () => {
        clearTimeout(gunRef.current.timer); gunRef.current.start = 0;
        const cleaned = orderNumber.trim();
        if (cleaned.length === 0) return;
        if (/^[0-9]{9}$/.test(cleaned) || cleaned.length >= 6) {
            return lookupOrder(cleaned);
        }
        setLastCode(cleaned);
        fail(t.badFormat);
    };

    const onSubmitCodeRef = useRef(onSubmitCode); onSubmitCodeRef.current = onSubmitCode;
    const onChangeRef = useRef(onChangeOrderNumber); onChangeRef.current = onChangeOrderNumber;
    // A scan typed while something else had focus (a button, the page) is not lost: the keys go to the scan box.
    useEffect(() => {
        if (mode !== "scan") return;
        const onKey = (e: KeyboardEvent) => {
            const el = document.activeElement as HTMLElement | null;
            if (!inputRef.current || el === inputRef.current) return;
            if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;   // typing in another field on purpose
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.key === "Enter") { if (inputRef.current.value.trim()) { e.preventDefault(); onSubmitCodeRef.current(); } return; }
            if (e.key.length !== 1) return;
            e.preventDefault();
            inputRef.current.focus();
            onChangeRef.current((inputRef.current.value || "") + e.key);
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [mode]);

    // Any click on the page (not on a control) sends focus back to the scanner
    // input, so a stray mouse click never "breaks" scanning.
    const refocus = useCallback(
        (e: React.MouseEvent) => {
            if (mode !== "scan") return;
            const el = e.target as HTMLElement;
            if (el.closest("button, a, input, select, textarea, summary, form")) return;
            inputRef.current?.focus();
        },
        [mode]
    );

    const [refreshing, setRefreshing] = useState(false);
    const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [left, setLeft] = useState<any>(null);   // what has not passed yet (Jumia ready-to-ship / Marjane prepared)
    const refreshNow = useCallback(async () => {
        setRefreshing(true);
        try {
            const r: any = await dispatch(actions.refreshNow);
            const failed = (r?.jumia || []).filter((x: any) => x.error).map((x: any) => x.store);
            setRefreshedAt(`${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}${failed.length ? ` · ما تحدّثش: ${failed.join("، ")}` : ""}`);
            setLeft(r?.remaining || null);
            setReloadKey((k) => k + 1);
            if (mode === "scan" && lastCode) lookupOrder(lastCode);
        } catch { setRefreshedAt("خطأ — عاود"); }
        setRefreshing(false);
    }, [mode, lastCode]);   // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div dir="rtl" lang="ar" className={`scanner-app scanner-app--${mode === "scan" ? verdict : "idle"}`} onClick={refocus}>
            {showSpinner && <Spinkit />}
            <Header stores={stores} setStores={setStores} setShowSpinner={setShowSpinner} mode={mode} setMode={setMode} />
            <CancelAlerts />
            {/* 🔄 every store's latest statuses at once (≈ 15 s) — the automatic pass walks the stores one by one */}
            <div className="mx-auto flex w-full max-w-5xl items-center gap-2 px-3 pt-3 sm:px-6">
                <button type="button" disabled={refreshing} onClick={refreshNow}
                    style={{ minHeight: 44, borderRadius: 12, border: "none", background: refreshing ? "#94a3b8" : "#1d4ed8", color: "#fff", fontWeight: 800, fontSize: 15, padding: "0 16px" }}>
                    {refreshing ? "⏳ كنتحقق من جميع المتاجر…" : "🔄 تحقق واش دازو الطلبات"}
                </button>
                {refreshedAt && <span style={{ fontSize: 13, color: "#475569" }}>آخر تحديث: {refreshedAt}</span>}
            </div>
            {left && (
                <div className="mx-auto w-full max-w-5xl px-3 pt-2 sm:px-6">
                    {left.jumia.pieces + left.marjan.orders === 0 ? (
                        <div style={{ background: "#dcfce7", color: "#166534", borderRadius: 12, padding: "10px 14px", fontWeight: 800 }}>✅ كلشي اللي توجد داز</div>
                    ) : (
                        <div style={{ background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 12, padding: "10px 14px" }}>
                            <div style={{ fontWeight: 900, color: "#9a3412", marginBottom: 4 }}>⚠️ توجدو وباقي ما دازوش: {left.jumia.pieces} قطعة جوميا · {left.marjan.orders} طلب مرجان</div>
                            {left.jumia.products.length > 0 && <div style={{ fontWeight: 800, marginTop: 4 }}>جوميا (Ready to ship):</div>}
                            {left.jumia.products.map((p: any) => <div key={`j${p.name}`} style={{ fontSize: 14 }}>• <b>{p.qty}×</b> {p.name} <span style={{ color: "#64748b", fontSize: 12 }}>— {p.orders.join("، ")}</span></div>)}
                            {left.marjan.products.length > 0 && <div style={{ fontWeight: 800, marginTop: 6 }}>مرجان (موجدين، مزال ما تسلموش):</div>}
                            {left.marjan.products.map((p: any) => <div key={`m${p.name}`} style={{ fontSize: 14 }}>• <b>{p.qty}×</b> {p.name} <span style={{ color: "#64748b", fontSize: 12 }}>— {p.orders.join("، ")}</span></div>)}
                        </div>
                    )}
                    {(left.jumia.forTomorrow + left.marjan.forTomorrow) > 0 && <div style={{ marginTop: 6, fontSize: 13, color: "#475569" }}>🕓 {left.jumia.forTomorrow + left.marjan.forTomorrow} طلب جا من بعد ما خرج الموزع{left.jumia.pickupAt ? ` (${new Date(left.jumia.pickupAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })})` : ""} — غادي يدوز غدا، ماشي مشكل</div>}
                </div>
            )}

            <main className="mx-auto w-full max-w-5xl px-3 pb-16 pt-4 sm:px-6">
                {mode === "packing" ? (
                    <Packing key={reloadKey} setShowSpinner={setShowSpinner} />
                ) : mode === "marjan" ? (
                    <MarjanPrep key={reloadKey} setShowSpinner={setShowSpinner} />
                ) : mode === "returns" ? (
                    <Returns key={reloadKey} setShowSpinner={setShowSpinner} />
                ) : mode === "alerts" && admin ? (
                    <Alerts key={reloadKey} setShowSpinner={setShowSpinner} />
                ) : mode === "find" ? (
                    <Find key={reloadKey} setShowSpinner={setShowSpinner} />
                ) : (
                    <>
                        <ScanInput
                            ref={inputRef}
                            value={orderNumber}
                            onChange={onChangeOrderNumber}
                            onSubmit={onSubmitCode}
                            refocusOn={[order, error]}
                        />

                        <Verdict
                            kind={verdict}
                            error={error}
                            lastCode={lastCode}
                            order={order}
                            store={foundStore}
                            groups={groups}
                            totalUnits={totalUnits}
                            rtsUnits={rtsUnits}
                        />

                        {order && groups.length > 0 && (
                            <section className="mt-5 space-y-5">
                                {groups.map((g, i) => (
                                    <Product key={g.key} group={g} order={order} index={i} total={groups.length} stock={g.sku in stockBySku ? stockBySku[g.sku] : undefined} url={urlBySku[g.sku]} groupName={groupBySku[g.sku]} variation={variationBySku[g.sku]} pack={packBySku[g.sku]} />
                                ))}
                            </section>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

export default ScannerApp;
